import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  DateFilterModule,
  IRowNode,
  NumberFilterModule,
  PaginationModule,
  QuickFilterModule,
  RowSelectionModule,
  RowSelectionOptions,
  TextFilterModule,
  ValidationModule,
  ExternalFilterModule,
  ModuleRegistry,
  themeAlpine,
  CustomFilterModule,
  CellSpanModule,
  CsvExportModule,
  ColumnApiModule,
} from 'ag-grid-community';
import { ClientSideRowModelModule } from 'ag-grid-community'; 
import { useSearchParams } from 'react-router-dom';
import { getRoomBooking, parseDate } from '../../services/Helper';
import { BadgeStatus } from '../BadgeStatus/BadgeStatus';
import { BadgeEventTitle } from '../BadgeEventTitle/BadgeEventTitle';
import { EventStatus, EventType, Role } from '../../services/Types';
import { BookingCategory, Event } from '../../services/Types';
import { ActionMenus } from '../ActionMenus/ActionMenus';
import { Alert, Box, Button, Grid } from '@mui/material';
import StatusFilter from './StatusFilter';
import { getStatusLabel } from '../../helpers/statusColors';
import './style.css';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useAllEvents } from '../../hooks/useAllEvents';
import { useDormant } from '../../hooks/useDormant';
import { useExportEvents } from '../../hooks/useExportEvents';
import { useEvent } from '../../hooks/useEvent';
import { useMyEvents } from '../../hooks/useMyEvents';
import { EventTypeToggle } from './EventTypeToggle';
import { SwitchPastEvents } from './SwitchPastEvents';
import { SwitchCancelledEvents } from './SwitchCancelledEvents';
import { SwitchSeriesEvents } from './SwitchSeriesEvents';
import { parseBooleanParam, parseArrayParam, parseDateParam, formatDateParam, updateSearchParams } from '../../utils/urlParams';
import { isPodcastEnabled } from '../../config/featureFlags';
ModuleRegistry.registerModules([
  PaginationModule,
  RowSelectionModule,
  QuickFilterModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ExternalFilterModule,
  CustomFilterModule,
  CellSpanModule,
  CsvExportModule,
  ColumnApiModule,
  ...(process.env.NODE_ENV !== "production" ? [ValidationModule] : []), /* Development Only */
]);

export const EventGrid = () => {
  const gridRef = useRef<AgGridReact>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const containerStyle = useMemo(() => ({ width: "100%", height: "calc(-265px + 100vh)" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Get default event types (all except SERIES)
  const getDefaultEventTypes = useCallback(() => {
    const allTypes = Object.values(EventType)
      .filter(eventType => eventType !== EventType.SERIES)
      .filter(eventType => isPodcastEnabled || eventType !== EventType.PODCAST);
    return ['all', ...allTypes];
  }, []);

  // Initialize state from URL params
  const [viewPastEvents, setViewPastEvents] = useState<boolean>(() => 
    parseBooleanParam(searchParams.get('pastEvents'), false)
  );
  const [viewCancelledEvents, setViewCancelledEvents] = useState<boolean>(() => 
    parseBooleanParam(searchParams.get('cancelledEvents'), false)
  );
  const [viewSeriesEvents, setViewSeriesEvents] = useState<boolean>(() => 
    parseBooleanParam(searchParams.get('seriesParents'), false)
  );
  const enableCellSpan = true;

  const { isPastEvent, getDisplayStatus } = useDormant();
  const [eventTypesChosen, setEventTypesChosen] = useState<string[]>(() => {
    const urlEventTypes = parseArrayParam(searchParams.get('eventType'), []);
    if (urlEventTypes.length === 0) {
      return getDefaultEventTypes();
    }
    // If 'all' is in the array, expand it to all types
    if (urlEventTypes.includes('all')) {
      return getDefaultEventTypes();
    }
    return urlEventTypes;
  });
  const [selectedEvents, setSelectedEvents] = useState<Event[]>([]);
  const [searchText, setSearchText] = useState<string>(() => 
    searchParams.get('q') || searchParams.get('search') || ''
  );
  const [statusFilter, setStatusFilter] = useState<EventStatus | null>(() => {
    const statusParam = searchParams.get('status');
    if (!statusParam || statusParam === 'all') {
      return null;
    }
    return statusParam as EventStatus;
  });
  const [dateFilterFrom, setDateFilterFrom] = useState<Date | null>(() =>
    parseDateParam(searchParams.get('dateFrom'), null)
  );
  const [dateFilterTo, setDateFilterTo] = useState<Date | null>(() =>
    parseDateParam(searchParams.get('dateTo'), null)
  );
  const [dateFilterType, setDateFilterType] = useState<string>(() =>
    searchParams.get('dateType') || 'equals'
  );
  const [sortBy, setSortBy] = useState<string | null>(() => 
    searchParams.get('sortBy') || null
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const order = searchParams.get('sortOrder');
    if (order === 'asc' || order === 'desc') {
      return order;
    }
    return viewPastEvents ? 'desc' : 'asc';
  });

  // Hook to fetch all events
  const allEvents = (user.role === Role.ADMIN || (user.role === Role.APPROVER && user.approverLevel > 1));
  // TODO: myEvents for admins should include everything (except blocked events). Can we use that?
  const { events: allEventsList, errorUseAllEvents: allEventsError, updating: allEventsUpdating } = useAllEvents();
  const { myEvents: myEventsList, errorUseMyEvents: myEventsError, updatingMyEvents: myEventsUpdating } = useMyEvents("all");
  const events = allEvents ? allEventsList : myEventsList;
  const errorUseAllEvents = allEvents ? allEventsError : myEventsError;
  const isLoading = allEventsUpdating || myEventsUpdating;

  // const { events, errorUseAllEvents } = useAllEvents();
  const { exportEventData } = useExportEvents();
  const { getEventData, errorUseEvent } = useEvent();
  const { getSeriesTitle } = useAllEvents();

  const handleSelectionChanged = async (event: any) => {
    const selectedRows = event.api.getSelectedRows();
    const expandedEvents = await Promise.all(
      selectedRows.map(async (row: Event) => {
        console.log("Loading event: ", row.id)
        // Only load full event data if not already loaded
        const eventData = await getEventData(row.id);
        return eventData;
      })
    );
    setSelectedEvents(expandedEvents);
  };

  // Set up the column definitions for the grid
  const defaultColDef = useMemo(() => ({
    filter: false,
    minWidth: 50
  }), []);

  // Sync search text to filter input and grid when searchText or events load. Only run deferred
  // path when data has just arrived (0 -> n) to avoid re-running on other filter changes.
  const prevEventsLengthRef = useRef(0);
  useEffect(() => {
    if (!searchText || !gridRef.current?.api) return;
    const input = document.getElementById("filter-text-box") as HTMLInputElement;
    if (input) input.value = searchText;

    const hadData = prevEventsLengthRef.current > 0;
    const hasData = events.length > 0;
    prevEventsLengthRef.current = events.length;

    const applyQuickFilter = () => {
      const api = gridRef.current?.api;
      if (!api || api.getGridOption?.("quickFilterText") === searchText) return;
      api.setGridOption("quickFilterText", searchText);
    };

    if (hasData && !hadData) {
      const t = setTimeout(applyQuickFilter, 100);
      return () => clearTimeout(t);
    }
    applyQuickFilter();
  }, [searchText, events.length]);

  // Skip applying filters when we're the source of the URL update (prevents refresh loop and popup closing)
  const skipNextFilterApplyRef = useRef(false);

  // Apply status and date filters from URL when: (1) events first load, or (2) user navigates (e.g. back button)
  // Do NOT run when handleFilterChanged updates URL - that causes a loop and closes the date filter popup
  useEffect(() => {
    if (skipNextFilterApplyRef.current) {
      skipNextFilterApplyRef.current = false;
      return;
    }
    if (!gridRef.current?.api || events.length === 0) return;

    setTimeout(() => {
      const statusParam = searchParams.get('status');
      const statusFromUrl = statusParam && statusParam !== 'all' ? statusParam : null;
      const dateFromParam = searchParams.get('dateFrom');
      const dateToParam = searchParams.get('dateTo');
      const dateTypeParam = searchParams.get('dateType') || 'equals';
      const dateFrom = parseDateParam(dateFromParam, null);
      const dateTo = dateToParam ? parseDateParam(dateToParam, null) : null;

      const currentModel = gridRef.current?.api.getFilterModel() || {};
      const newModel = { ...currentModel };
      if (statusFromUrl) {
        newModel.status = statusFromUrl;
      } else {
        delete newModel.status;
      }
      if (dateFrom) {
        const dateFromStr = formatDateParam(dateFrom);
        const dateToStr = dateTo ? formatDateParam(dateTo) : undefined;
        newModel.date = {
          filterType: 'date',
          type: dateTypeParam,
          dateFrom: `${dateFromStr} 00:00:00`,
          ...(dateToStr && { dateTo: `${dateToStr} 23:59:59` }),
        };
      } else {
        delete newModel.date;
      }
      gridRef.current?.api.setFilterModel(newModel);
      gridRef.current?.api.onFilterChanged();
    }, 100);
  }, [events.length, searchParams]);

  // Update URL params when state changes
  const updateURLParams = useCallback((updates: Record<string, string | number | boolean | string[] | null | undefined>) => {
    const newParams = updateSearchParams(searchParams, updates);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Handle filter changes to update URL (for status and date filters)
  const handleFilterChanged = useCallback(() => {
    if (gridRef.current?.api) {
      const filterModel = gridRef.current.api.getFilterModel();
      const statusValue = filterModel?.status;
      const dateFilter = filterModel?.date;
      const updates: Record<string, string | null | undefined> = {};
      if (statusValue !== statusFilter) {
        setStatusFilter(statusValue || null);
        updates.status = statusValue || null;
      }
      if (dateFilter?.dateFrom) {
        const dateFromStr = String(dateFilter.dateFrom).split(' ')[0]?.split('T')[0] || '';
        const dateToStr = dateFilter.dateTo ? String(dateFilter.dateTo).split(' ')[0]?.split('T')[0] || '' : '';
        const dateFrom = parseDateParam(dateFromStr, null);
        const dateTo = dateToStr ? parseDateParam(dateToStr, null) : null;
        const type = dateFilter.type || 'equals';
        const newDateFrom = dateFrom ? formatDateParam(dateFrom) : null;
        const newDateTo = dateTo ? formatDateParam(dateTo) : null;
        setDateFilterFrom(dateFrom);
        setDateFilterTo(dateTo);
        setDateFilterType(type);
        updates.dateFrom = newDateFrom;
        updates.dateTo = newDateTo;
        updates.dateType = type;
      } else if (dateFilterFrom) {
        setDateFilterFrom(null);
        setDateFilterTo(null);
        setDateFilterType('equals');
        updates.dateFrom = null;
        updates.dateTo = null;
        updates.dateType = null;
      }
      if (Object.keys(updates).length > 0) {
        skipNextFilterApplyRef.current = true;
        updateURLParams(updates);
      }
    }
  }, [statusFilter, dateFilterFrom, updateURLParams]);

  // Apply sort from URL only when events first load. Do not depend on sortBy/sortOrder so that
  // user sort clicks only update state/URL via handleSortChanged and don't retrigger this effect
  // (which would call applyColumnState again and can cause URL to flip between eventDate and creater).
  const prevEventsLengthForSortRef = useRef(0);
  useEffect(() => {
    if (!gridRef.current?.api || events.length === 0) return;
    const hadData = prevEventsLengthForSortRef.current > 0;
    prevEventsLengthForSortRef.current = events.length;
    if (hadData) return; // Only run when data has just arrived

    const t = setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      if (sortBy) {
        const colId = sortBy === 'eventDate' ? 'date' : sortBy;
        api.applyColumnState({
          state: [{ colId, sort: sortOrder }],
          defaultState: { sort: null }
        });
      } else {
        const defaultSortOrder = viewPastEvents ? 'desc' : 'asc';
        api.applyColumnState({
          state: [{ colId: 'date', sort: defaultSortOrder }],
          defaultState: { sort: null }
        });
      }
    }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only apply sort when events first load; sortBy/sortOrder/viewPastEvents read for initial apply only to avoid loop with handleSortChanged
  }, [events.length]);

  const columnDefs: ColDef<Event>[] = [
    { field: 'id' as keyof Event,
      headerName: 'ID',
      hide: true,
      lockPosition: true,
      cellDataType: "number",
    },
    { field: 'seriesId',
      headerName: 'Series',
      width: 120,
      // spanRows: ({ valueA, valueB }) => {
      //   return valueA === valueB && valueA && valueB;
      // },
      cellRenderer: (params: any) => {
        if (params?.data?.seriesId) {
          return getSeriesTitle(params.data?.seriesId);
        }
        return '';
      },
    },
    { field: 'title',
      headerName: 'Event',
      minWidth: 290,
      cellRenderer: (params: any) => <BadgeEventTitle eventType={params.data.eventType} eventTitle={params.data.title} seriesId={params.data.seriesId} />,
    },
    {
      colId: 'date',
      headerName: 'Event Date',
      minWidth: 190,
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['equals', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual', 'inRange'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        // Compare date-only (UTC) so "Equals" works when cell has time component; aligns with formatDate display (MM/DD/YYYY from UTC)
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date | null) => {
          if (cellValue == null) return 1;
          const cellY = cellValue.getUTCFullYear(), cellM = cellValue.getUTCMonth(), cellD = cellValue.getUTCDate();
          const filterY = filterLocalDateAtMidnight.getUTCFullYear(), filterM = filterLocalDateAtMidnight.getUTCMonth(), filterD = filterLocalDateAtMidnight.getUTCDate();
          const cellDay = Date.UTC(cellY, cellM, cellD);
          const filterDay = Date.UTC(filterY, filterM, filterD);
          if (cellDay === filterDay) return 0;
          return cellDay < filterDay ? -1 : 1;
        },
      },
      filterValueGetter: (params: any) => {
        const roomBooking = getRoomBooking(params.data?.roomBooking, BookingCategory.LIVE);
        return roomBooking ? parseDate(roomBooking.date) : null;
      },
      valueGetter: (params: any) => {
        const roomBooking = getRoomBooking(params.data?.roomBooking, BookingCategory.LIVE);
        return roomBooking ? roomBooking.date + roomBooking.startTime : 0;
      },
      cellRenderer: (params: any) => {
        const roomBooking = getRoomBooking(params.data?.roomBooking, BookingCategory.LIVE);
        return roomBooking ? roomBooking.formattedDate + ' | ' + roomBooking.formattedStartTime : '';
      },
      getQuickFilterText: (params: any) => {
        const roomBooking = getRoomBooking(params.data?.roomBooking, BookingCategory.LIVE);
        return roomBooking
          ? `date:${roomBooking.formattedDate} ${roomBooking.formattedDate} | ${roomBooking.formattedStartTime}`
          : '';
      },
      // Only set sort when this column is the active sort; otherwise leave undefined so we don't re-apply date sort and trigger onSortChanged again (which would overwrite URL with eventDate)
      sort: sortBy === 'eventDate' ? sortOrder : undefined
    },
    { 
      field: 'creater',
      headerName: 'Requestor',
      minWidth: 190,
      valueGetter: (params: any) =>
        `${params.data?.creater?.firstName} ${params.data?.creater?.lastName}`,
      getQuickFilterText: (params: any) =>
        `${params.data?.creater?.firstName} ${params.data?.creater?.lastName}`,
    },
    {
      field: 'programOffice',
      headerName: 'Program Office',
      valueGetter: (params: any) => {
        return params.data?.programOffice ? params.data?.programOffice?.title : '';
      },
      getQuickFilterText: (params: any) => {
        return params.data?.programOffice ? params.data?.programOffice?.title : '';
      },
      minWidth: 190
    },
    { field: 'status',
      headerName: 'Status',
      cellRenderer: (params: any) => <BadgeStatus status={getDisplayStatus(params.data, viewPastEvents)} />,
      getQuickFilterText: (params: any) => {
        const displayStatus = getDisplayStatus(params.data, viewPastEvents);
        if (!displayStatus) return '';
        const label = getStatusLabel(displayStatus);
        return `status:${displayStatus} ${displayStatus} ${label}`;
      },
      minWidth: 190,
      filter: StatusFilter,
      filterParams: { viewPastEvents, getDisplayStatus }
    },
    {
      headerName: "Actions",
      width: 100,
      cellRenderer: (params: any) => <ActionMenus eventData={params.data} />,
      pinned: 'right',
    }
  ];

  const rowSelection: RowSelectionOptions = {
    mode: "multiRow",
    headerCheckbox: true,
    selectAll: 'filtered',
  };

  // Quick filter text box handler
  const onFilterTextBoxChanged = useCallback(() => {
    const value = (document.getElementById("filter-text-box") as HTMLInputElement).value;
    setSearchText(value);
    gridRef.current!.api.setGridOption("quickFilterText", value);
    updateURLParams({ q: value || null });
  }, [updateURLParams]);

  // View past events checkbox handler
  const viewPastEventsFilterChanged = useCallback((newValue: boolean) => {
    setViewPastEvents(newValue);
    // Update default sort order based on past events
    const newSortOrder = newValue ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    gridRef.current!.api.onFilterChanged();
    updateURLParams({ pastEvents: newValue, sortOrder: newSortOrder });
  }, [updateURLParams]);

  // View cancelled events checkbox handler
  const viewCancelledEventsFilterChanged = useCallback((newValue: boolean) => {
    setViewCancelledEvents(newValue);
    gridRef.current!.api.onFilterChanged();
    updateURLParams({ cancelledEvents: newValue });
  }, [updateURLParams]);

  // View series events checkbox handler
  const viewSeriesEventsFilterChanged = useCallback((newValue: boolean) => {
    setViewSeriesEvents(newValue);
    gridRef.current!.api.onFilterChanged();
    updateURLParams({ seriesParents: newValue });
  }, [updateURLParams]);

  // Event type filter handler
  const eventTypeFilterChanged = useCallback(
    (event: React.MouseEvent<HTMLElement>, newTypes: string[]) => {
      let finalTypes: string[];
      if (newTypes.includes('all')) {
        finalTypes = getDefaultEventTypes();
      } else {
        finalTypes = newTypes;
      }
      setEventTypesChosen(finalTypes);
      gridRef.current!.api.onFilterChanged();
      // Store in URL - if all types are selected, store 'all', otherwise store the array
      const urlValue = finalTypes.length === getDefaultEventTypes().length ? ['all'] : finalTypes;
      updateURLParams({ eventType: urlValue });
    },
    [getDefaultEventTypes, updateURLParams]
  );

  // Handle sort changes from ag-grid
  const handleSortChanged = useCallback((event: any) => {
    const columnState = gridRef.current?.api.getColumnState();
    const sortedColumn = columnState?.find((col) => col.sort);
    if (sortedColumn) {
      const colId = sortedColumn.colId || '';
      const sortOrder = sortedColumn.sort || 'asc';
      const sortByParam = colId === 'date' ? 'eventDate' : colId;
      setSortBy(sortByParam);
      setSortOrder(sortOrder as 'asc' | 'desc');
      updateURLParams({ sortBy: sortByParam, sortOrder: sortOrder });
    }
  }, [updateURLParams]);

  // Boolean to check if external filter is present... and it always is because viewPastEvents is a state
  const isExternalFilterPresent = useCallback((): boolean => {
    return true;
  }, []);

  // External filter handler for both eventType and viewPastEvents
  const doesExternalFilterPass = useCallback(
    (node: IRowNode<Event>): boolean => {
      // default to showing all rows even without any dates or eventTypes
      let showThisRow = true;

      // only show events that match the viewPastEvents state
      // Use 180-day cutoff for ALL events: eventDate or updatedAt must be 180+ days ago (WFGE-11691)
      const past = isPastEvent(node.data);
      showThisRow = viewPastEvents ? past : !past;

      // Handle cancelled events filter
      if (viewCancelledEvents) {
        // If cancelled events switch is on, only show cancelled events
        showThisRow = showThisRow && node.data?.status === EventStatus.CANCELLED;
      } else {
        showThisRow = showThisRow && node.data?.status !== EventStatus.CANCELLED;
      }

      // Handle series events filter
      if (viewSeriesEvents) {
        // If series events switch is on, only show series events
        showThisRow = showThisRow && node.data?.eventType === EventType.SERIES;
      } else {
        showThisRow = showThisRow && node.data?.eventType !== EventType.SERIES;
      }

      // Handle event type filtering
      if (node.data?.eventType) {
        // Check if this event type is in the chosen types
        const isEventTypeChosen = eventTypesChosen.includes(node.data.eventType as EventType);
        showThisRow = showThisRow && isEventTypeChosen;
      }

      return showThisRow;
    },
    [eventTypesChosen, viewPastEvents, viewCancelledEvents, viewSeriesEvents, isPastEvent],
  );

  // Ensure external filter is applied on initial load when state comes from URL
  useEffect(() => {
    if (gridRef.current?.api && events && events.length > 0) {
      gridRef.current.api.onFilterChanged();
    }
  }, [events, eventTypesChosen, viewPastEvents, viewCancelledEvents, viewSeriesEvents]);

  return (
    <div className="ag-theme-alpine" style={containerStyle}>

      {errorUseAllEvents && <Alert variant="filled" severity="error">{errorUseAllEvents}</Alert>}
      {errorUseEvent && <Alert variant="filled" severity="error">{errorUseEvent}</Alert>}

      <Grid container className="event-grid-header">
        <Grid item xs={12} md={6} className="event-grid-header-checkboxes">
          <EventTypeToggle
            eventTypesChosen={eventTypesChosen}
            onChange={eventTypeFilterChanged}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <div className="event-grid-header-buttons">
            {user.role === Role.APPROVER && (
              <Button variant="contained" color="primary" disabled={selectedEvents.length === 0} onClick={() => {}}>Approve</Button>
            )}
            <Button variant="contained" color="primary" disabled={selectedEvents.length === 0} onClick={() => exportEventData(selectedEvents, { searchParams })}>Export</Button>
          </div>
          <div className="event-grid-header-switch-container">
            <SwitchPastEvents
              viewPastEvents={viewPastEvents}
              onChange={(value) => viewPastEventsFilterChanged(value)}
            />
            <SwitchCancelledEvents
              viewCancelledEvents={viewCancelledEvents}
              onChange={(value) => viewCancelledEventsFilterChanged(value)}
            />
            <SwitchSeriesEvents
              viewSeriesEvents={viewSeriesEvents}
              onChange={(value) => viewSeriesEventsFilterChanged(value)}
            />
          </div>
        </Grid>
      </Grid>

      <Box className="event-grid-container" style={gridStyle}>
        <TextField
          type="text"
          id="filter-text-box"
          placeholder="Search by event title, requestor, date, status or program office."
          variant="standard"
          defaultValue={searchText}
          onInput={onFilterTextBoxChanged}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: '100%', background: 'transparent', padding: '5px 0',
            "& .MuiInputBase-root": {
              "& input": {
                border: "none",
              },
              "&.Mui-focused input": {
                border: "none",
              },
              "&:hover input": {
                border: "none",
              },
            },
          }}
        />
        <AgGridReact
          ref={gridRef}
          theme={themeAlpine}
          rowData={events}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={isLoading}
          rowSelection={rowSelection}
          onSelectionChanged={handleSelectionChanged}
          onSortChanged={handleSortChanged}
          onFilterChanged={handleFilterChanged}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          enableCellSpan={enableCellSpan}
          scrollbarWidth={8}
        />
      </Box>
    </div>
  );
}; 